import { useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, useAnimations } from '@react-three/drei';
import * as THREE from 'three';
import SolarStructure from './SolarStructure';

export default function SolarStructureScene(props) {
  return (
    <Canvas shadows camera={{ position: [10, 6, 10], fov: 45 }}>
      <ambientLight intensity={0.1} />
      <SunLight />
      <OrbitControls enableZoom={true} />
      <SceneGroup {...props} />
    </Canvas>
  );
}

function SunLight() {
  const lightRef = useRef();

  useFrame(({ clock }) => {
    const elapsed = clock.getElapsedTime();
    const dayProgress = (elapsed / 24) * Math.PI * 2;
    const radius = 20;

    const x = radius * Math.cos(dayProgress - Math.PI / 2);
    const y = radius * Math.sin(dayProgress - Math.PI / 2);
    const z = 10;

    if (lightRef.current) {
      lightRef.current.position.set(x, y, z);
      const intensity = Math.max(0, y / radius);
      lightRef.current.intensity = intensity;
      const color = new THREE.Color();
      color.setHSL(intensity < 0.5 ? 0.1 : 0.6, intensity < 0.5 ? 0.8 : 0.1, 1);
      lightRef.current.color = color;
    }
  });

  return (
    <directionalLight
      ref={lightRef}
      castShadow
      shadow-mapSize-width={2048}
      shadow-mapSize-height={2048}
      shadow-camera-left={-30}
      shadow-camera-right={30}
      shadow-camera-top={30}
      shadow-camera-bottom={-30}
      shadow-camera-near={1}
      shadow-camera-far={100}
      intensity={1}
      position={[0, 20, 10]}
    />
  );
}

function HumanModel({ totalWidth, zPosition }) {
  const ref = useRef();
  const { scene, animations } = useGLTF(
    'https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Models/master/2.0/CesiumMan/glTF-Binary/CesiumMan.glb',
  );

  const { actions } = useAnimations(animations, ref);

  useEffect(() => {
    if (actions && actions['animation_0']) {
      actions['animation_0'].play();
    }
  }, [actions]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const speed = 1;
    const distance = totalWidth + 4.5;
    const x = ((t * speed) % distance) - distance / 4;

    if (ref.current) {
      ref.current.position.set(x, 0, zPosition);
      ref.current.rotation.y = Math.PI / 2;
    }
  });

  return (
    <primitive
      ref={ref}
      object={scene}
      scale={[0.9, 0.9, 0.9]}
      position={[-totalWidth / 2 - 2, 0, 0]}
    />
  );
}

function SceneGroup({
  setStructureData,
  humanZPosition,
  tableCols,
  tableRows,
  tileAngleDeg,
  lowestLegHeight,
}) {
  const groupRef = useRef();
  useFrame(({ clock }) => {
    const time = clock.getElapsedTime();
    groupRef.current.rotation.y = time * 0.08;
  });

  const spacingX = 2;
  const totalWidth = spacingX * (tableCols - 1);

  return (
    <group ref={groupRef}>
      <SolarStructure
        setStructureData={setStructureData}
        tableCols={tableCols}
        tableRows={tableRows}
        tileAngleDeg={tileAngleDeg}
        lowestLegHeight={lowestLegHeight}
      />
      <HumanModel totalWidth={totalWidth} zPosition={humanZPosition} />
    </group>
  );
}
